/**
 * Merges the executor's authoritative open-trigger list with the backend's
 * position enrichment into the Aevo-shaped `{ order_id, trigger }` the UI reads.
 *
 * `positions[].triggers` is ABSENT (never null) both when there are no triggers
 * AND when the executor is briefly unreachable, so its absence alone must never
 * be read as "cancelled" — the list is preferred and the enrichment only fills
 * gaps.
 */
import { GetAccount200ResponsePositionsInner, SideResponse } from "../../codegen-api";
import { getMarketAddress } from "../../config/chains";
import { formatFixed18 } from "./tpsl.signing";
import { TpslOrder, TpslPriceSource, TpslTriggerType } from "./tpsl.types";

export interface TpslTriggerView {
  order_id: string;
  trigger: string;
  /**
   * Leaf-v2 extras, used to prefill the modal and to decide whether an edit is
   * really a no-op. Only the executor list carries them — the backend's
   * position enrichment does not, so they stay undefined on that path.
   */
  priceSource?: TpslPriceSource;
  /** Human decimal, "" when the leg has no execution bound. */
  limitPrice?: string;
  /** Human decimal, "" / "0" when the leg closes the whole position. */
  sizeInTokens?: string;
}

export type TpslTriggersView = Partial<
  Record<TpslTriggerType, TpslTriggerView>
>;

const isLivePrice = (value?: string): boolean => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
};

export function selectTpslTriggers(
  openOrders: TpslOrder[],
  position: GetAccount200ResponsePositionsInner | undefined
): TpslTriggersView {
  if (!position) {
    return {};
  }

  const view: TpslTriggersView = {};
  const market = getMarketAddress(position.instrument_name);
  const isLong = position.side === SideResponse.Buy;

  if (market) {
    openOrders
      .filter(
        (order) =>
          String(order.market || "").toLowerCase() === market.toLowerCase() &&
          Boolean(order.isLong) === isLong
      )
      .forEach((order) => {
        // The executor answers FIXED-18 integers; the enrichment below is
        // already human. Normalize here so everything downstream (the cell, the
        // modal prefill, the no-op-edit comparison) speaks one unit — otherwise
        // a $90,000 take profit renders as $9e+22 and every edit looks like a
        // change.
        const trigger = formatFixed18(order.triggerPrice);
        if (order.id && isLivePrice(trigger)) {
          const limitPrice = formatFixed18(order.limitPrice);
          const sizeInTokens = formatFixed18(order.sizeInTokensToClose);
          view[order.triggerType] = {
            order_id: order.id,
            trigger,
            priceSource: order.priceSource,
            limitPrice: isLivePrice(limitPrice) ? limitPrice : "",
            sizeInTokens: isLivePrice(sizeInTokens) ? sizeInTokens : "",
          };
        }
      });
  }

  const enrichment: [TpslTriggerType, TpslTriggerView | undefined][] = [
    ["take_profit", position.triggers?.take_profit],
    ["stop_loss", position.triggers?.stop_loss],
  ];
  enrichment.forEach(([type, trigger]) => {
    if (!view[type] && trigger?.order_id && isLivePrice(trigger.trigger)) {
      view[type] = { order_id: trigger.order_id, trigger: trigger.trigger };
    }
  });

  return view;
}
